import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { classificationsApi } from '../api/classifications';
import { useCompany } from '../contexts/CompanyContext';
import { registerClassificationDefs } from '../components/badges';
import type { ClassificationDef, ClassificationKind } from '../types';

/**
 * The company's classification pick-lists — the values behind the ticket
 * form's Type, Priority, Source, Business impact and Environment dropdowns.
 *
 * One query serves every consumer (react-query dedupes on the key), and it
 * also feeds the badge registry so a renamed type or recolored priority shows
 * up on chips anywhere, the same way custom statuses do.
 */
export function useClassifications() {
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data, isLoading } = useQuery({
    queryKey: ['classifications', companyId],
    queryFn: () => classificationsApi.list(),
    enabled: !!currentCompany,
    staleTime: 5 * 60 * 1000,
  });

  const defs = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (defs.length > 0) registerClassificationDefs(defs);
  }, [defs]);

  return useMemo(() => {
    const byKind = (kind: ClassificationKind) => defs.filter(d => d.kind === kind);

    /**
     * Options for a picker: the active values, plus `current` when the ticket
     * already carries something that has since been turned off — otherwise
     * editing that ticket would silently change its type.
     */
    const optionsFor = (kind: ClassificationKind, current?: string | null): ClassificationDef[] => {
      const all = byKind(kind);
      const active = all.filter(d => d.isActive);
      if (current && !active.some(d => d.key === current)) {
        const stale = all.find(d => d.key === current);
        if (stale) return [...active, stale];
      }
      return active;
    };

    const labelOf = (kind: ClassificationKind, key?: string | null): string => {
      if (!key) return '';
      return byKind(kind).find(d => d.key === key)?.label ?? key.replace(/_/g, ' ');
    };

    const defaultKeyOf = (kind: ClassificationKind): string => {
      const active = byKind(kind).filter(d => d.isActive);
      return (active.find(d => d.isDefault) ?? active[0])?.key ?? '';
    };

    return { defs, isLoading, byKind, optionsFor, labelOf, defaultKeyOf };
  }, [defs, isLoading]);
}

export type { ClassificationDef, ClassificationKind };
